import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    Heart, Trash2, User, Trophy, Loader2, ChevronRight, 
    Calendar, MapPin, TrendingUp, Award, Clock, X, Star,
    BarChart3, Target, Percent, Bell, Zap, Users, Flame,
    Snowflake, AlertTriangle, ArrowRight, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { format, parseISO, differenceInMinutes } from 'date-fns';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== Portfolio Stats Dashboard ====================
const PortfolioStats = ({ stats }) => {
    if (!stats) return null;
    
    return (
        <div className="space-y-4">
            {/* Main Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardContent className="p-4 text-center">
                        <BarChart3 className="h-6 w-6 mx-auto text-primary mb-2" />
                        <p className="font-data text-3xl font-bold">{stats.total_runs}</p>
                        <p className="text-sm text-muted-foreground">Total Runs</p>
                    </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/20">
                    <CardContent className="p-4 text-center">
                        <Trophy className="h-6 w-6 mx-auto text-amber-500 mb-2" />
                        <p className="font-data text-3xl font-bold">{stats.total_wins}</p>
                        <p className="text-sm text-muted-foreground">Wins</p>
                    </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/20">
                    <CardContent className="p-4 text-center">
                        <Percent className="h-6 w-6 mx-auto text-emerald-500 mb-2" />
                        <p className="font-data text-3xl font-bold">{stats.combined_win_rate}%</p>
                        <p className="text-sm text-muted-foreground">Win Rate</p>
                    </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20">
                    <CardContent className="p-4 text-center">
                        <Target className="h-6 w-6 mx-auto text-blue-500 mb-2" />
                        <p className="font-data text-3xl font-bold">{stats.combined_place_rate}%</p>
                        <p className="text-sm text-muted-foreground">Place Rate</p>
                    </CardContent>
                </Card>
            </div>
            
            {/* Best Performer & Hot/Cold Streaks */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {stats.best_performer && stats.best_performer.wins > 0 && (
                    <Card className="border-amber-500/30 bg-amber-500/5">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                                    <Star className="h-5 w-5 text-amber-500" />
                                </div>
                                <div>
                                    <p className="text-xs text-amber-600 font-medium uppercase">Best Performer</p>
                                    <p className="font-semibold">{stats.best_performer.name}</p>
                                    <p className="text-sm text-muted-foreground">
                                        {stats.best_performer.win_rate}% win rate
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}
                
                {stats.hot_streak?.length > 0 && (
                    <Card className="border-orange-500/30 bg-orange-500/5">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                                    <Flame className="h-5 w-5 text-orange-500" />
                                </div>
                                <div>
                                    <p className="text-xs text-orange-600 font-medium uppercase">Hot Streak 🔥</p>
                                    <p className="font-semibold">{stats.hot_streak.map(p => p.name).join(', ')}</p>
                                    <p className="text-sm text-muted-foreground">
                                        Winning recently
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}
                
                {stats.cold_streak?.length > 0 && (
                    <Card className="border-blue-400/30 bg-blue-400/5">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-400/20 flex items-center justify-center">
                                    <Snowflake className="h-5 w-5 text-blue-400" />
                                </div>
                                <div>
                                    <p className="text-xs text-blue-600 font-medium uppercase">Cold Streak ❄️</p>
                                    <p className="font-semibold">{stats.cold_streak.map(p => p.name).join(', ')}</p>
                                    <p className="text-sm text-muted-foreground">
                                        Out of form
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
};

// ==================== Notifications Panel ====================
const NotificationsPanel = ({ notifications }) => {
    if (!notifications || notifications.length === 0) return null;
    
    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'border-red-500/50 bg-red-500/5';
            case 'medium': return 'border-amber-500/50 bg-amber-500/5';
            default: return 'border-muted';
        }
    };
    
    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                    <Bell className="h-5 w-5 text-primary" />
                    Alerts & Notifications
                    <Badge variant="secondary" className="ml-auto">{notifications.length}</Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                {notifications.map((notif, idx) => (
                    <Link key={idx} to={`/race/${notif.event_id}`}>
                        <div className={`p-3 rounded-lg border ${getPriorityColor(notif.priority)} hover:bg-muted/50 transition-colors cursor-pointer`}>
                            <p className="text-sm">{notif.message}</p>
                        </div>
                    </Link>
                ))}
            </CardContent>
        </Card>
    );
};

// ==================== Upcoming Races Section ====================
const UpcomingRacesSection = ({ races }) => {
    if (!races || races.length === 0) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <Calendar className="h-5 w-5 text-primary" />
                        Upcoming Races
                    </CardTitle>
                </CardHeader>
                <CardContent className="text-center py-8">
                    <Clock className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No upcoming races for your tracked items today</p>
                    <p className="text-sm text-muted-foreground mt-1">Check back when declarations are made</p>
                </CardContent>
            </Card>
        );
    }
    
    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                    <Calendar className="h-5 w-5 text-primary" />
                    Upcoming Races
                    <Badge variant="secondary" className="ml-auto">{races.length}</Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {races.map((race, idx) => {
                    const raceTime = parseISO(race.start_time);
                    const minsUntil = differenceInMinutes(raceTime, new Date());
                    const isImminent = minsUntil <= 30;
                    
                    return (
                        <Link key={idx} to={`/race/${race.event_id}`} state={{ courseName: race.stage_name }}>
                            <div className={`p-4 rounded-lg border hover:bg-muted/50 transition-colors ${isImminent ? 'border-red-500/50 bg-red-500/5' : ''}`}>
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Badge variant={isImminent ? "destructive" : "outline"} className="font-data">
                                                {format(raceTime, 'HH:mm')}
                                            </Badge>
                                            <span className="font-medium">{race.stage_name}</span>
                                            {isImminent && <Badge variant="destructive" className="text-xs">Soon!</Badge>}
                                        </div>
                                        <p className="text-sm text-muted-foreground line-clamp-1">{race.event_title}</p>
                                        <div className="flex items-center gap-2 mt-2">
                                            <Badge variant="secondary" className="text-xs capitalize">
                                                {race.tracked_type}: {race.tracked_item}
                                            </Badge>
                                            {race.tracked_type !== 'horse' && (
                                                <span className="text-xs text-muted-foreground">on {race.horse_name}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        {race.odds && (
                                            <p className="font-data text-lg font-bold text-primary">{race.odds}</p>
                                        )}
                                        <ChevronRight className="h-4 w-4 text-muted-foreground mt-1 ml-auto" />
                                    </div>
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </CardContent>
        </Card>
    );
};

// ==================== Performance Comparison ====================
const PerformanceComparison = ({ data }) => {
    if (!data || data.length === 0) return null;
    
    const maxWinRate = Math.max(...data.map(d => d.win_rate), 1);
    
    const getTypeIcon = (type) => {
        switch (type) {
            case 'horse': return Trophy;
            case 'jockey': return User;
            case 'trainer': return Award;
            default: return Heart;
        }
    };
    
    const getTypeColor = (type) => {
        switch (type) {
            case 'horse': return 'bg-amber-500';
            case 'jockey': return 'bg-blue-500';
            case 'trainer': return 'bg-purple-500';
            default: return 'bg-primary';
        }
    };
    
    const getFormIcon = (form) => {
        switch (form) {
            case 'hot': return <Flame className="h-4 w-4 text-orange-500" />;
            case 'cold': return <Snowflake className="h-4 w-4 text-blue-400" />;
            default: return null;
        }
    };
    
    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    Performance Comparison
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {data.map((item, idx) => {
                    const Icon = getTypeIcon(item.type);
                    const barWidth = maxWinRate > 0 ? (item.win_rate / maxWinRate) * 100 : 0;
                    
                    return (
                        <div key={idx} className="space-y-2">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Icon className={`h-4 w-4 ${item.type === 'horse' ? 'text-amber-500' : item.type === 'jockey' ? 'text-blue-500' : 'text-purple-500'}`} />
                                    <span className="font-medium text-sm">{item.name}</span>
                                    {getFormIcon(item.recent_form)}
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-muted-foreground">{item.runs}R</span>
                                    <span className="font-data font-bold">{item.win_rate}%</span>
                                </div>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                    className={`h-full ${getTypeColor(item.type)} transition-all duration-500`}
                                    style={{ width: `${barWidth}%` }}
                                />
                            </div>
                            {/* Recent form dots */}
                            {item.recent_results?.length > 0 && (
                                <div className="flex items-center gap-1">
                                    <span className="text-xs text-muted-foreground mr-1">Recent:</span>
                                    {item.recent_results.slice(0, 5).map((pos, i) => (
                                        <div 
                                            key={i}
                                            className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-data font-bold
                                                ${pos === 1 ? 'bg-amber-500 text-white' : 
                                                  pos <= 3 ? 'bg-emerald-500/20 text-emerald-600' : 
                                                  'bg-muted text-muted-foreground'}`}
                                        >
                                            {pos}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
};

// ==================== Dream Team Section ====================
const DreamTeamSection = ({ teams }) => {
    if (!teams || teams.length === 0) return null;
    
    return (
        <Card className="border-primary/20 bg-primary/5">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                    <Sparkles className="h-5 w-5 text-primary" />
                    Dream Team Combinations
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {teams.map((team, idx) => (
                    <div key={idx} className="p-4 rounded-lg bg-background border">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <Award className="h-4 w-4 text-purple-500" />
                                <span className="font-medium">{team.trainer}</span>
                                <span className="text-muted-foreground">+</span>
                                <User className="h-4 w-4 text-blue-500" />
                                <span className="font-medium">{team.jockey}</span>
                            </div>
                            <Badge className="bg-primary text-primary-foreground font-data">
                                {team.combined_score}%
                            </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{team.insight}</p>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
};

// ==================== Profile Modal (kept from original) ====================
const ProfileModal = ({ item, onClose, onRemove }) => {
    const [loading, setLoading] = useState(true);
    const [profileData, setProfileData] = useState(null);
    const [upcomingRaces, setUpcomingRaces] = useState([]);

    useEffect(() => {
        const fetchProfileData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(
                    `${API}/racing/participant/${encodeURIComponent(item.item_name)}/results`,
                    { params: { participant_type: item.item_type, days_back: 3 } }
                );
                
                const data = response.data;
                setUpcomingRaces((data.upcoming || []).map(r => ({
                    eventId: r.event_id,
                    eventTitle: r.event_title,
                    stageName: r.stage_name,
                    startTime: r.start_time,
                    horse: r.horse_name,
                    odds: r.odds
                })));
                
                setProfileData({
                    recentResults: (data.results || []).map(r => ({
                        eventId: r.event_id,
                        eventTitle: r.event_title,
                        stageName: r.stage_name,
                        startTime: r.start_time,
                        horse: r.horse_name,
                        result: r.result
                    })),
                    stats: data.stats
                });
            } catch (err) {
                console.error('Failed to fetch profile data:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchProfileData();
    }, [item]);

    const getIcon = () => {
        switch (item.item_type) {
            case 'horse': return Trophy;
            case 'jockey': return User;
            case 'trainer': return Award;
            default: return Heart;
        }
    };
    
    const Icon = getIcon();
    const getOrdinal = (n) => n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                <CardHeader className="sticky top-0 bg-card z-10 border-b">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                                <Icon className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                                <CardTitle className="text-xl">{item.item_name}</CardTitle>
                                <p className="text-sm text-muted-foreground capitalize">{item.item_type}</p>
                            </div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={onClose}>
                            <X className="h-5 w-5" />
                        </Button>
                    </div>
                </CardHeader>
                
                <CardContent className="p-4 space-y-6">
                    {loading ? (
                        <div className="space-y-4">
                            <Skeleton className="h-24 w-full" />
                            <Skeleton className="h-32 w-full" />
                        </div>
                    ) : (
                        <>
                            {profileData?.stats && (
                                <div className="grid grid-cols-4 gap-3">
                                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                                        <p className="font-data text-xl font-bold">{profileData.stats.total_runs}</p>
                                        <p className="text-xs text-muted-foreground">Runs</p>
                                    </div>
                                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                                        <p className="font-data text-xl font-bold">{profileData.stats.wins}</p>
                                        <p className="text-xs text-muted-foreground">Wins</p>
                                    </div>
                                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                                        <p className="font-data text-xl font-bold">{profileData.stats.places}</p>
                                        <p className="text-xs text-muted-foreground">Places</p>
                                    </div>
                                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                                        <p className="font-data text-xl font-bold">{profileData.stats.win_rate}%</p>
                                        <p className="text-xs text-muted-foreground">Win Rate</p>
                                    </div>
                                </div>
                            )}

                            <div>
                                <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                                    <TrendingUp className="h-4 w-4 text-primary" />
                                    Recent Results
                                </h3>
                                {profileData?.recentResults?.length === 0 ? (
                                    <p className="text-sm text-muted-foreground text-center py-4">No recent results</p>
                                ) : (
                                    <div className="space-y-2">
                                        {profileData?.recentResults?.map((result, idx) => (
                                            <Link key={idx} to={`/race/${result.eventId}`} state={{ courseName: result.stageName }} onClick={onClose}>
                                                <Card className="hover:bg-muted/50 cursor-pointer">
                                                    <CardContent className="p-3">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <Badge 
                                                                        variant={result.result?.position === 1 ? 'default' : 'secondary'}
                                                                        className={result.result?.position === 1 ? 'bg-amber-500' : ''}
                                                                    >
                                                                        {result.result?.position ? `${result.result.position}${getOrdinal(result.result.position)}` : 'N/A'}
                                                                    </Badge>
                                                                    <span className="text-sm font-medium">{result.stageName}</span>
                                                                </div>
                                                                <p className="text-sm text-muted-foreground line-clamp-1">{result.eventTitle}</p>
                                                            </div>
                                                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-3 pt-4 border-t">
                                <Button 
                                    variant="destructive" 
                                    className="flex-1 gap-2"
                                    onClick={() => { onRemove(item); onClose(); }}
                                >
                                    <Trash2 className="h-4 w-4" />
                                    Remove from Stable
                                </Button>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

// ==================== Stable Item Component ====================
const StableItem = ({ item, onClick, onRemove }) => {
    const ICONS = { horse: Trophy, jockey: User, trainer: Award, default: Heart };
    const Icon = ICONS[item.item_type] || ICONS.default;

    return (
        <div 
            className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors cursor-pointer group"
            onClick={onClick}
            data-testid={`stable-item-${item.id}`}
        >
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                    <p className="font-medium group-hover:text-primary transition-colors">{item.item_name}</p>
                    <p className="text-sm text-muted-foreground capitalize">
                        {item.item_type} • Added {format(parseISO(item.added_at), 'MMM d')}
                    </p>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => { e.stopPropagation(); onRemove(item); }}
                    className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                >
                    <Trash2 className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
};

// ==================== Main Page Component ====================
export default function StablePage() {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [stableItems, setStableItems] = useState([]);
    const [insights, setInsights] = useState(null);
    const [loading, setLoading] = useState(true);
    const [insightsLoading, setInsightsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('insights');
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login', { state: { from: { pathname: '/stable' } } });
            return;
        }

        const fetchData = async () => {
            try {
                const stableRes = await axios.get(`${API}/stable`);
                setStableItems(stableRes.data.items || []);
            } catch (error) {
                console.error('Failed to fetch stable:', error);
            } finally {
                setLoading(false);
            }
        };
        
        const fetchInsights = async () => {
            try {
                const insightsRes = await axios.get(`${API}/stable/insights`);
                setInsights(insightsRes.data);
            } catch (error) {
                console.error('Failed to fetch insights:', error);
            } finally {
                setInsightsLoading(false);
            }
        };
        
        fetchData();
        fetchInsights();
    }, [isAuthenticated, navigate]);

    const handleRemoveFromStable = async (item) => {
        try {
            await axios.delete(`${API}/stable/${item.id}`);
            setStableItems(prev => prev.filter(i => i.id !== item.id));
            toast.success(`Removed ${item.item_name} from your stable`);
        } catch (error) {
            toast.error('Failed to remove from stable');
        }
    };

    const horses = stableItems.filter(i => i.item_type === 'horse');
    const jockeys = stableItems.filter(i => i.item_type === 'jockey');
    const trainers = stableItems.filter(i => i.item_type === 'trainer');

    if (loading) {
        return (
            <div className="container-app py-8 flex items-center justify-center min-h-[50vh]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen pb-20 md:pb-8">
            <div className="container-app py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="font-heading text-3xl font-bold page-title">My Stable</h1>
                        <p className="text-muted-foreground">
                            Track performance of your favourite horses, jockeys and trainers
                        </p>
                    </div>
                    <Badge variant="secondary" className="font-data text-lg px-3 py-1">
                        {stableItems.length} tracked
                    </Badge>
                </div>

                {stableItems.length === 0 ? (
                    <Card className="text-center py-16">
                        <Heart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <h3 className="font-heading text-lg font-semibold mb-2">Your stable is empty</h3>
                        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                            Add horses, jockeys or trainers to your stable to track their performance.
                            Click the heart icon on any racecard to get started!
                        </p>
                        <Link to="/races">
                            <Button className="btn-pill" data-testid="browse-races-btn">
                                Browse Today&apos;s Races
                            </Button>
                        </Link>
                    </Card>
                ) : (
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <TabsList className="mb-6">
                            <TabsTrigger value="insights" data-testid="tab-insights">
                                <Zap className="h-4 w-4 mr-2" />
                                Insights
                            </TabsTrigger>
                            <TabsTrigger value="stable" data-testid="tab-stable">
                                <Heart className="h-4 w-4 mr-2" />
                                Stable ({stableItems.length})
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="insights" className="space-y-6">
                            {insightsLoading ? (
                                <div className="space-y-4">
                                    <Skeleton className="h-32 w-full" />
                                    <Skeleton className="h-48 w-full" />
                                    <Skeleton className="h-64 w-full" />
                                </div>
                            ) : (
                                <>
                                    {/* Portfolio Stats Dashboard */}
                                    {insights?.portfolio_stats && (
                                        <PortfolioStats stats={insights.portfolio_stats} />
                                    )}
                                    
                                    {/* Notifications */}
                                    {insights?.notifications?.length > 0 && (
                                        <NotificationsPanel notifications={insights.notifications} />
                                    )}
                                    
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {/* Upcoming Races */}
                                        <UpcomingRacesSection races={insights?.upcoming_races} />
                                        
                                        {/* Performance Comparison */}
                                        <PerformanceComparison data={insights?.performance_data} />
                                    </div>
                                    
                                    {/* Dream Teams */}
                                    {insights?.dream_teams?.length > 0 && (
                                        <DreamTeamSection teams={insights.dream_teams} />
                                    )}
                                </>
                            )}
                        </TabsContent>

                        <TabsContent value="stable">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Horses */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-lg">
                                            <Trophy className="h-5 w-5 text-amber-500" />
                                            Horses ({horses.length})
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        {horses.length === 0 ? (
                                            <div className="text-center py-6">
                                                <Trophy className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                                                <p className="text-sm text-muted-foreground">No horses added yet</p>
                                            </div>
                                        ) : (
                                            horses.map(item => (
                                                <StableItem 
                                                    key={item.id} 
                                                    item={item} 
                                                    onClick={() => setSelectedItem(item)}
                                                    onRemove={handleRemoveFromStable}
                                                />
                                            ))
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Jockeys */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-lg">
                                            <User className="h-5 w-5 text-blue-500" />
                                            Jockeys ({jockeys.length})
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        {jockeys.length === 0 ? (
                                            <div className="text-center py-6">
                                                <User className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                                                <p className="text-sm text-muted-foreground">No jockeys added yet</p>
                                            </div>
                                        ) : (
                                            jockeys.map(item => (
                                                <StableItem 
                                                    key={item.id} 
                                                    item={item} 
                                                    onClick={() => setSelectedItem(item)}
                                                    onRemove={handleRemoveFromStable}
                                                />
                                            ))
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Trainers */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-lg">
                                            <Award className="h-5 w-5 text-purple-500" />
                                            Trainers ({trainers.length})
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        {trainers.length === 0 ? (
                                            <div className="text-center py-6">
                                                <Award className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                                                <p className="text-sm text-muted-foreground">No trainers added yet</p>
                                            </div>
                                        ) : (
                                            trainers.map(item => (
                                                <StableItem 
                                                    key={item.id} 
                                                    item={item} 
                                                    onClick={() => setSelectedItem(item)}
                                                    onRemove={handleRemoveFromStable}
                                                />
                                            ))
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>
                    </Tabs>
                )}
            </div>

            {/* Profile Modal */}
            {selectedItem && (
                <ProfileModal 
                    item={selectedItem} 
                    onClose={() => setSelectedItem(null)}
                    onRemove={handleRemoveFromStable}
                />
            )}
        </div>
    );
}
